/**
 * Records a telehealth session's start/end from its session call (owner
 * decision 2026-10-20) — on the PROVIDER's device, whichever party dialled:
 *
 * - the call CONNECTS → `check-in` with `startedBy: 'call'` at the connect
 *   moment (the server only links the call when the session already started);
 * - a deliberate hang-up (either side) → `check-out` with `endedBy: 'call'`;
 * - a drop, an error, or the peer vanishing (`endCause: 'peer-lost'`) waits
 *   {@link SESSION_CALL_RECONNECT_WINDOW_MS}: a session call for the same
 *   session connecting — including a plain redial with the same person —
 *   cancels it, otherwise the end is recorded at the moment the call dropped;
 * - the recording-consent window ending the call undoes a call-recorded start
 *   and records NO end (owner decision): the provider records the times. A
 *   consent END that lands just after the call already ended another way
 *   (MeetingEnded raced it) re-classifies that end.
 *
 * Durable across restarts: pending ends and the live session call (with a
 * `lastSeenAt` heartbeat) are persisted PER SIGNED-IN USER, so a killed app
 * still settles the session — at `lastSeenAt` once the call is gone — and an
 * account switch never submits another user's ends. A pending end is only
 * dropped after the server answered 2xx / 409 (or a definitive 4xx; never a
 * 401, which refreshes the session and retries, nor a WAF rate-limit 403);
 * network and 5xx failures retry with backoff and on resume / back online.
 *
 * Each recorded start/end opens a short Undo ({@link SESSION_CALL_UNDO_MS})
 * that reverts through the existing `undo-check-in` / `undo-check-out` paths.
 * Framework-free: the React controller feeds it voice-call snapshots.
 */

import {
  planSessionCallEnd,
  SESSION_CALL_RECONNECT_WINDOW_MS,
  SESSION_CALL_UNDO_MS,
  type SessionCallEndPlan,
} from '@shared/sessions/session-call'
import type { SessionLifecycleEvent } from '@shared/sessions/types'
import type { LifecycleEventResult } from '../data/sessions-repository'
import { CONSENT_RECLASSIFY_WINDOW_MS } from '../../../utils/consent-window-end'

/** The voice-call fields the recorder reads. */
export interface RecorderCallSnapshot {
  callId: string
  phase: string
  isGroup?: boolean
  sessionUuid?: string | null
  localRole?: 'caller' | 'callee'
  /** The other party (redial matching; natively answered rings). */
  peerUserId?: string
  connectedAt?: number
  endedAt?: number
  endReason?: string
  endCause?: string
}

export type SessionCallNotice =
  | { id: number; kind: 'started' | 'ended'; sessionUuid: string; undoUntil: number; undoing: boolean }
  | { id: number; kind: 'info' | 'error'; message: string }
  /** The call can't stand as the session: record the times by hand. */
  | { id: number; kind: 'manual'; sessionUuid: string; message: string }

export interface SessionCallRecorderDeps {
  /** Signed-in user: persisted state is namespaced to, and filtered by, this id. */
  userId: string
  submit: (event: SessionLifecycleEvent) => Promise<LifecycleEventResult>
  /** Force-refresh the auth session (a 401 retries once after it). */
  refreshAuth?: () => Promise<unknown>
  /** Session of a call whose ring was rebuilt natively (no INVITE payload in JS). */
  resolveSessionUuid: (callId: string, peerUserId: string | undefined) => Promise<string | null>
  /** A call without `sessionUuid` turned out to be the session call (redial / native ring). */
  onSessionResolved?: (callId: string, sessionUuid: string) => void
  /** Whether the server still holds `callId` as a live call (heartbeat-fresh). */
  isCallLive?: (callId: string) => Promise<boolean>
  onResult?: (result: LifecycleEventResult) => void
  now?: () => number
  newEventId: () => string
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
}

export interface SessionCallRecorder {
  observe: (snapshot: RecorderCallSnapshot | null) => void
  undo: () => Promise<void>
  dismissNotice: () => void
  getNotice: () => SessionCallNotice | null
  subscribe: (listener: () => void) => () => void
  /** Settle (or re-arm) pending ends and orphaned live calls from a previous run. */
  restorePending: () => void
  /** Retry due pending ends now (app resumed / back online). */
  retryPendingNow: () => void
  /** The server's consent window ended `callId` — possibly after it already ended here. */
  reclassifyAsConsentEnd: (callId: string) => void
  dispose: () => void
}

export const PENDING_END_STORAGE_KEY = 'ascend-session-call-pending-ends'
export const ACTIVE_CALL_STORAGE_KEY = 'ascend-session-call-active'
export const CONSENT_ENDED_MESSAGE =
  "Recording consent wasn't given, so the call ended. Record the session times manually."
export const NOT_LINKED_MESSAGE =
  "This call couldn't be recorded as the session. Record the session times manually."
const ERROR_START_MESSAGE =
  "Couldn't record the start time from the call. Record it on the session's Attendance card."
const NOTICE_MS = 5_000
/** How often a live session call refreshes its persisted `lastSeenAt`. */
export const ACTIVE_HEARTBEAT_MS = 15_000
/** After a restart, give the call this long to rehydrate before settling it. */
export const REHYDRATE_GRACE_MS = 30_000
/** Persisted entries older than this are discarded unsent. */
export const SESSION_CALL_STATE_MAX_AGE_MS = 24 * 60 * 60_000
const RETRY_BACKOFF_MS = [15_000, 30_000, 60_000, 120_000, 300_000]
/** 409 codes meaning "already settled" — nothing for the provider to fix. */
const SETTLED_CONFLICTS: ReadonlySet<string> = new Set(['INVALID_TRANSITION', 'UNDO_WINDOW_EXPIRED'])

/** This user's storage keys (persisted state never crosses accounts). */
export function sessionCallStorageKeys(userId: string): { pending: string; active: string } {
  return { pending: `${PENDING_END_STORAGE_KEY}:${userId}`, active: `${ACTIVE_CALL_STORAGE_KEY}:${userId}` }
}

/**
 * Drop every account's persisted session-call state (sign-out when the
 * signed-out user id could not be read).
 */
export function clearAllSessionCallRecorderStorage(
  storage: Pick<Storage, 'length' | 'key' | 'removeItem'> | null | undefined = typeof window !== 'undefined'
    ? window.localStorage
    : null,
): void {
  if (!storage) return
  const doomed: string[] = []
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i)
    if (key && (key.startsWith(`${PENDING_END_STORAGE_KEY}:`) || key.startsWith(`${ACTIVE_CALL_STORAGE_KEY}:`))) {
      doomed.push(key)
    }
  }
  for (const key of doomed) storage.removeItem(key)
}

/** Drop a user's persisted session-call state (sign-out). */
export function clearSessionCallRecorderStorage(
  userId: string,
  storage: Pick<Storage, 'removeItem'> | null | undefined = typeof window !== 'undefined' ? window.localStorage : null,
): void {
  const keys = sessionCallStorageKeys(userId)
  storage?.removeItem(keys.pending)
  storage?.removeItem(keys.active)
}

interface PendingEnd {
  owner: string
  sessionUuid: string
  callId: string
  droppedAt: number
  deadline: number
  /** This call's connect, on the same device clock as `droppedAt`. */
  connectedAt?: number
  peerUserId?: string
  attempts?: number
  /** Ask the server whether the call is still live before ending (drops / restarts). */
  checkLive?: boolean
}

interface ActiveRecord {
  owner: string
  sessionUuid: string
  callId: string
  connectedAt?: number
  peerUserId?: string
  lastSeenAt: number
}

interface TrackedCall {
  sessionUuid: string | null
  connectedAt: number
  settled: boolean
  /** When this device saw the call end (bounds a late consent re-classification). */
  endedSeenAt: number | null
  /** How the call's end was planned (re-classified by a late consent END). */
  plan: SessionCallEndPlan | null
  /** This call's connect recorded the start (so it owns the start's undo). */
  startApplied: boolean
  /** Resolves once the start submit (and any session lookup) finished. */
  started: Promise<void>
}

function errorStatus(err: unknown): number | null {
  const status = (err as { status?: unknown } | null)?.status
  return typeof status === 'number' ? status : null
}

function errorCode(err: unknown): string | null {
  const code = (err as { code?: unknown } | null)?.code
  return typeof code === 'string' ? code : null
}

/**
 * Server answered and the outcome is final. Never a 401 (expired session),
 * and a 403 only when it is the Lambda's own refusal (`error: 'FORBIDDEN'`):
 * the Sessions API WAF answers a rate-limited request with a bare 403.
 */
function isFinalRejection(err: unknown): boolean {
  const status = errorStatus(err)
  if (status == null) return false
  if (status === 403) return errorCode(err) === 'FORBIDDEN'
  return status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429
}

/** A final rejection that leaves the session for the provider to fix by hand. */
function needsManualTimes(err: unknown): boolean {
  const code = errorCode(err)
  return !(errorStatus(err) === 409 && code != null && SETTLED_CONFLICTS.has(code))
}

export function createSessionCallRecorder(deps: SessionCallRecorderDeps): SessionCallRecorder {
  const now = deps.now ?? (() => Date.now())
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms))
  const clearTimer = deps.clearTimer ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>))
  const owner = deps.userId
  const keys = sessionCallStorageKeys(owner)
  const tracked = new Map<string, TrackedCall>()
  const pendingTimers = new Map<string, unknown>()
  const inFlight = new Set<string>()
  const listeners = new Set<() => void>()
  let last: RecorderCallSnapshot | null = null
  let notice: SessionCallNotice | null = null
  let noticeTimer: unknown = null
  let heartbeatTimer: unknown = null
  let noticeSeq = 0
  let disposed = false

  const emit = () => {
    for (const listener of listeners) listener()
  }

  const setNotice = (next: SessionCallNotice | null, ttlMs?: number) => {
    if (noticeTimer != null) clearTimer(noticeTimer)
    noticeTimer = null
    notice = next
    if (next && ttlMs != null) {
      const id = next.id
      noticeTimer = setTimer(() => {
        if (notice?.id === id) {
          notice = null
          emit()
        }
      }, ttlMs)
    }
    emit()
  }

  const showMessage = (kind: 'info' | 'error', message: string) =>
    setNotice({ id: ++noticeSeq, kind, message }, NOTICE_MS)

  const showManual = (sessionUuid: string, message: string) =>
    setNotice({ id: ++noticeSeq, kind: 'manual', sessionUuid, message })

  const showUndo = (kind: 'started' | 'ended', sessionUuid: string) =>
    setNotice(
      { id: ++noticeSeq, kind, sessionUuid, undoUntil: now() + SESSION_CALL_UNDO_MS, undoing: false },
      SESSION_CALL_UNDO_MS,
    )

  // --- persistence (this user's keys; foreign / stale entries ignored) -------
  const readList = <T extends { owner: string }>(
    key: string,
    valid: (item: unknown) => item is T,
    seenAt: (item: T) => number,
  ): T[] => {
    let parsed: unknown
    try {
      const raw = deps.storage?.getItem(key)
      parsed = raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
    if (!Array.isArray(parsed)) return []
    const kept = parsed.filter(
      (item): item is T => valid(item) && item.owner === owner && now() - seenAt(item) <= SESSION_CALL_STATE_MAX_AGE_MS,
    )
    if (kept.length !== parsed.length) writeList(key, kept)
    return kept
  }
  const writeList = (key: string, list: unknown[]) => {
    try {
      if (list.length === 0) deps.storage?.removeItem(key)
      else deps.storage?.setItem(key, JSON.stringify(list))
    } catch {
      /* storage unavailable: in-memory timers still settle this run */
    }
  }
  const isPending = (p: unknown): p is PendingEnd => {
    const v = p as Partial<PendingEnd> | null
    return (
      typeof v?.owner === 'string' &&
      typeof v.sessionUuid === 'string' &&
      typeof v.callId === 'string' &&
      typeof v.droppedAt === 'number' &&
      typeof v.deadline === 'number'
    )
  }
  const isActive = (a: unknown): a is ActiveRecord => {
    const v = a as Partial<ActiveRecord> | null
    return (
      typeof v?.owner === 'string' &&
      typeof v.sessionUuid === 'string' &&
      typeof v.callId === 'string' &&
      typeof v.lastSeenAt === 'number'
    )
  }
  const readPending = () => readList(keys.pending, isPending, (p) => p.droppedAt)
  const writePending = (list: PendingEnd[]) => writeList(keys.pending, list)
  const savePending = (p: PendingEnd) =>
    writePending([...readPending().filter((x) => x.sessionUuid !== p.sessionUuid), p])
  const readActive = () => readList(keys.active, isActive, (a) => a.lastSeenAt)
  const writeActive = (list: ActiveRecord[]) => writeList(keys.active, list)

  const removePending = (sessionUuid: string) => {
    const timer = pendingTimers.get(sessionUuid)
    if (timer != null) clearTimer(timer)
    pendingTimers.delete(sessionUuid)
    writePending(readPending().filter((p) => p.sessionUuid !== sessionUuid))
  }

  /** Submit; an expired session (401) is refreshed and retried once. */
  const submit = async (event: Omit<SessionLifecycleEvent, 'clientEventId'>) => {
    const full = { ...event, clientEventId: deps.newEventId() }
    let result: LifecycleEventResult
    try {
      result = await deps.submit(full)
    } catch (err) {
      if (errorStatus(err) !== 401 || !deps.refreshAuth) throw err
      await deps.refreshAuth().catch(() => undefined)
      result = await deps.submit(full)
    }
    deps.onResult?.({ ...result, eventType: event.type })
    return result
  }

  // --- pending ends ----------------------------------------------------------
  const arm = (pending: PendingEnd) => {
    const existing = pendingTimers.get(pending.sessionUuid)
    if (existing != null) clearTimer(existing)
    pendingTimers.set(
      pending.sessionUuid,
      setTimer(() => {
        pendingTimers.delete(pending.sessionUuid)
        void attempt(pending.sessionUuid)
      }, Math.max(0, pending.deadline - now())),
    )
  }

  const attempt = async (sessionUuid: string) => {
    if (disposed || inFlight.has(sessionUuid)) return
    const pending = readPending().find((p) => p.sessionUuid === sessionUuid)
    if (!pending) return
    inFlight.add(sessionUuid)
    try {
      // The server still has the call live (the peer is on it, or this device
      // has not rehydrated yet): the session is not over — wait again.
      if (
        pending.checkLive &&
        deps.isCallLive &&
        (await deps.isCallLive(pending.callId).catch(() => false))
      ) {
        const next = { ...pending, deadline: now() + SESSION_CALL_RECONNECT_WINDOW_MS }
        savePending(next)
        arm(next)
        return
      }
      const result = await submit({
        uuid: pending.sessionUuid,
        type: 'check-out',
        endedBy: 'call',
        callId: pending.callId,
        occurredAt: pending.droppedAt,
        ...(pending.connectedAt != null ? { callConnectedAt: pending.connectedAt } : {}),
      })
      removePending(sessionUuid)
      if (!disposed && result.status === 'applied') showUndo('ended', pending.sessionUuid)
    } catch (err) {
      if (isFinalRejection(err)) {
        // Final: 409 already-settled is silent; anything else (not linked,
        // refused) leaves the times to the provider.
        removePending(sessionUuid)
        if (!disposed && needsManualTimes(err)) showManual(pending.sessionUuid, NOT_LINKED_MESSAGE)
        return
      }
      const attempts = (pending.attempts ?? 0) + 1
      const next = {
        ...pending,
        attempts,
        deadline: now() + RETRY_BACKOFF_MS[Math.min(attempts - 1, RETRY_BACKOFF_MS.length - 1)],
      }
      savePending(next)
      arm(next)
    } finally {
      inFlight.delete(sessionUuid)
    }
  }

  const queueEnd = (pending: PendingEnd) => {
    savePending(pending)
    if (pending.deadline <= now()) void attempt(pending.sessionUuid)
    else arm(pending)
  }

  // --- live session call heartbeat (restart recovery) -------------------------
  const heartbeat = () => {
    heartbeatTimer = null
    if (disposed) return
    const active = readActive()
    if (active.length === 0) return
    const liveId = last && last.phase === 'active' ? last.callId : null
    writeActive(active.map((a) => (a.callId === liveId ? { ...a, lastSeenAt: now() } : a)))
    heartbeatTimer = setTimer(heartbeat, ACTIVE_HEARTBEAT_MS)
  }
  const ensureHeartbeat = () => {
    if (heartbeatTimer == null) heartbeatTimer = setTimer(heartbeat, ACTIVE_HEARTBEAT_MS)
  }
  const forgetActive = (callId: string) => writeActive(readActive().filter((a) => a.callId !== callId))

  // --- call transitions -------------------------------------------------------
  const onConnected = (snapshot: RecorderCallSnapshot) => {
    if (tracked.has(snapshot.callId)) return
    if (tracked.size >= 20) {
      for (const [id, old] of tracked) {
        if (old.settled) {
          tracked.delete(id)
          break
        }
      }
    }
    const call: TrackedCall = {
      sessionUuid: snapshot.sessionUuid ?? null,
      connectedAt: snapshot.connectedAt ?? now(),
      settled: false,
      endedSeenAt: null,
      plan: null,
      startApplied: false,
      started: Promise.resolve(),
    }
    tracked.set(snapshot.callId, call)
    call.started = (async () => {
      // A plain redial (Recents / Call back) with the same person while the
      // session waits for a reconnect IS the reconnect — provisionally: the
      // server re-verifies (same two parties, connected inside the window)
      // before the pending end is let go.
      let viaRedial = false
      if (!call.sessionUuid && snapshot.peerUserId) {
        call.sessionUuid =
          readPending().find((p) => p.peerUserId && p.peerUserId === snapshot.peerUserId)?.sessionUuid ?? null
        viaRedial = call.sessionUuid != null
      }
      if (!call.sessionUuid && snapshot.localRole === 'callee') {
        call.sessionUuid = await deps
          .resolveSessionUuid(snapshot.callId, snapshot.peerUserId)
          .catch(() => null)
        if (call.sessionUuid) deps.onSessionResolved?.(snapshot.callId, call.sessionUuid)
      }
      const sessionUuid = call.sessionUuid
      if (!sessionUuid || disposed) return
      const track = () => {
        // Reconnected inside the window: the session carries on.
        removePending(sessionUuid)
        writeActive([
          ...readActive().filter((a) => a.sessionUuid !== sessionUuid),
          {
            owner,
            sessionUuid,
            callId: snapshot.callId,
            connectedAt: call.connectedAt,
            peerUserId: snapshot.peerUserId,
            lastSeenAt: now(),
          },
        ])
        ensureHeartbeat()
        if (viaRedial) deps.onSessionResolved?.(snapshot.callId, sessionUuid)
      }
      if (!viaRedial) track()
      try {
        const result = await submit({
          uuid: sessionUuid,
          type: 'check-in',
          startedBy: 'call',
          callId: snapshot.callId,
          occurredAt: call.connectedAt,
        })
        if (viaRedial) track()
        call.startApplied = result.status === 'applied'
        if (!disposed && call.startApplied && !call.settled) showUndo('started', sessionUuid)
      } catch (err) {
        if (viaRedial && isFinalRejection(err)) {
          // Not this session's call after all: an ordinary call — the
          // pending end stands.
          call.sessionUuid = null
          return
        }
        if (viaRedial) track()
        if (disposed) return
        // Already started (fallback, or a previous connect): 409 settled
        // conflicts are link-only — don't toast "Couldn't record the start".
        if (isFinalRejection(err) && !needsManualTimes(err)) return
        if (isFinalRejection(err) && needsManualTimes(err)) showManual(sessionUuid, NOT_LINKED_MESSAGE)
        else showMessage('error', ERROR_START_MESSAGE)
      }
    })()
  }

  /** Owner decision: the call can't stand as the session. */
  const applyConsentPlan = async (sessionUuid: string, call: TrackedCall) => {
    if (call.startApplied) {
      await submit({ uuid: sessionUuid, type: 'undo-check-in', occurredAt: now() }).catch(() => undefined)
    }
    if (!disposed) showManual(sessionUuid, CONSENT_ENDED_MESSAGE)
  }

  const onEnded = (
    callId: string,
    endReason: string | undefined,
    endCause: string | undefined,
    endedAt: number | undefined,
    peerUserId: string | undefined,
  ) => {
    const call = tracked.get(callId)
    if (!call || call.settled) return
    call.settled = true
    call.endedSeenAt = now()
    const at = endedAt ?? now()
    const plan = planSessionCallEnd(endReason, endCause)
    call.plan = plan
    void call.started.then(async () => {
      const sessionUuid = call.sessionUuid
      forgetActive(callId)
      if (!sessionUuid || disposed) return
      if (plan === 'consent-missing') {
        await applyConsentPlan(sessionUuid, call)
        return
      }
      queueEnd({
        owner,
        sessionUuid,
        callId,
        droppedAt: at,
        connectedAt: call.connectedAt,
        deadline: plan === 'record-now' ? now() : at + SESSION_CALL_RECONNECT_WINDOW_MS,
        peerUserId,
        checkLive: plan !== 'record-now',
      })
    })
  }

  /**
   * The consent END arrived after the call had already ended another way
   * (e.g. MeetingEnded first → peer-lost): replace the unsent pending end with
   * the consent plan. An end that was already sent stays; the prompt still
   * tells the provider to fix the times.
   */
  const reclassifyAsConsentEnd = (callId: string) => {
    const call = tracked.get(callId)
    if (!call || !call.settled || call.plan === 'consent-missing') return
    if (call.endedSeenAt == null || now() - call.endedSeenAt > CONSENT_RECLASSIFY_WINDOW_MS) return
    call.plan = 'consent-missing'
    void call.started.then(async () => {
      const sessionUuid = call.sessionUuid
      if (!sessionUuid || disposed) return
      const pending = readPending().find((p) => p.sessionUuid === sessionUuid && p.callId === callId)
      if (pending && !inFlight.has(sessionUuid)) removePending(sessionUuid)
      await applyConsentPlan(sessionUuid, call)
    })
  }

  return {
    observe(snapshot) {
      const prev = last
      last = snapshot
      if (snapshot && !snapshot.isGroup) {
        if (snapshot.phase === 'active') onConnected(snapshot)
        else if (snapshot.phase === 'ended') {
          if (snapshot.endReason === 'consent-missing' && tracked.get(snapshot.callId)?.settled) {
            reclassifyAsConsentEnd(snapshot.callId)
          } else {
            onEnded(snapshot.callId, snapshot.endReason, snapshot.endCause, snapshot.endedAt, snapshot.peerUserId)
          }
        }
      }
      // Vanished without an 'ended' snapshot (store cleared): treat as a drop.
      if (prev && prev.phase === 'active' && (!snapshot || snapshot.callId !== prev.callId)) {
        onEnded(prev.callId, undefined, undefined, now(), prev.peerUserId)
      }
    },
    async undo() {
      const current = notice
      if (!current || (current.kind !== 'started' && current.kind !== 'ended') || current.undoing) return
      if (now() > current.undoUntil) return
      setNotice({ ...current, undoing: true }, Math.max(0, current.undoUntil - now()))
      try {
        await submit({
          uuid: current.sessionUuid,
          type: current.kind === 'started' ? 'undo-check-in' : 'undo-check-out',
          occurredAt: now(),
        })
        showMessage('info', current.kind === 'started' ? 'Start time undone' : 'End time undone')
      } catch {
        showMessage('error', "Couldn't undo — the undo window has passed. Correct it from the session's Attendance card.")
      }
    },
    dismissNotice() {
      setNotice(null)
    },
    getNotice: () => notice,
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    restorePending() {
      // A session call that was live when this app died: settle it at its
      // last heartbeat once the call is gone (a rehydrate cancels this).
      const orphans = readActive()
      writeActive([])
      for (const orphan of orphans) {
        if (readPending().some((p) => p.sessionUuid === orphan.sessionUuid)) continue
        savePending({
          owner,
          sessionUuid: orphan.sessionUuid,
          callId: orphan.callId,
          connectedAt: orphan.connectedAt,
          peerUserId: orphan.peerUserId,
          droppedAt: orphan.lastSeenAt,
          deadline: Math.max(orphan.lastSeenAt + SESSION_CALL_RECONNECT_WINDOW_MS, now() + REHYDRATE_GRACE_MS),
          checkLive: true,
        })
      }
      for (const pending of readPending()) arm(pending)
    },
    reclassifyAsConsentEnd(callId) {
      reclassifyAsConsentEnd(callId)
    },
    retryPendingNow() {
      for (const pending of readPending()) {
        if (pending.deadline <= now() || (pending.attempts ?? 0) > 0) {
          const timer = pendingTimers.get(pending.sessionUuid)
          if (timer != null) clearTimer(timer)
          pendingTimers.delete(pending.sessionUuid)
          void attempt(pending.sessionUuid)
        }
      }
    },
    dispose() {
      disposed = true
      for (const timer of pendingTimers.values()) clearTimer(timer)
      pendingTimers.clear()
      if (noticeTimer != null) clearTimer(noticeTimer)
      if (heartbeatTimer != null) clearTimer(heartbeatTimer)
      listeners.clear()
    },
  }
}
